// HF-Session-Leak-1 (2026-07-31), updated for HF-Session-Leak-2 (2026-09-16)
// ============================================================================
// Client data was surviving the end of a session in two places:
//
//   1. localStorage — "solara.recentDeals" (deal references + client company
//      names, QuickCaptureFab.jsx) and "solara_draft" (a whole in-progress
//      loan application: guarantor names, DOBs, addresses, assets/liabilities,
//      security valuations). logout() removed neither. Same failure mode as
//      the hidden-mailbox incident, where a leaked preference from one user's
//      session changed what a different user saw.
//
//   2. (HISTORICAL — root-caused and closed differently by HF-Session-Leak-2,
//      see below) The service worker's API cache: sw.js used to cache every
//      OK GET keyed by URL ONLY, with no auth material in the key, and
//      protectedFile.js routes downloaded client PDFs through /api/.
//      `caches.` appeared nowhere in src/, so nothing ever evicted them: sign
//      out, hand the laptop over, and the next person's browser could serve
//      a previous client's document straight from cache.
//
// This module is the one place that tears down (1). Call it whenever the
// identity behind the session changes — sign-out, and also sign-in as a
// DIFFERENT user on the same browser, which is the case a logout-only fix
// misses entirely (shared machine, someone just closed the tab).
//
// HF-Session-Leak-2 (2026-09-16) — clearApiCaches() below was (2)'s fix, and
// it was a MITIGATION, not a root-cause fix: it only ran on an explicit
// logout or a detected identity switch (a DIFFERENT person logging in), so
// it did nothing for a borrower who simply closed the tab without logging
// out — the next, unrelated person on that device inherited both a
// still-valid year-long JWT (see backend/config.py) AND that borrower's
// cached, PII-bearing API responses. That combination is what caused the
// 2026-09-16 incident. The actual fix is now in sw.js's handleApi(): an
// authenticated GET is never written to API_CACHE in the first place, so
// there is nothing identity-scoped left in that cache for clearApiCaches()
// to need to catch after the fact. clearApiCaches() is kept below anyway —
// it's still correct to run on identity change, it's cheap, and it also
// clears the app-shell-adjacent caches of anything that slips in — but it
// is no longer the thing standing between one client's PII and the next
// person on a shared device. Do not rely on it for that; the sw.js fix is.

// localStorage keys that hold user-specific data. Exact matches and prefixes
// are both supported — draft keys are per-user suffixed ("solara_draft:<id>").
const USER_SCOPED_EXACT = [
  "solara.recentDeals",
  "solara_draft", // legacy unscoped key — purge on sight
];

const USER_SCOPED_PREFIXES = [
  "solara_draft:",
  "solara_draft_step",
];

/** Remove every user-scoped localStorage key. Never throws. */
export function clearUserScopedStorage() {
  try {
    USER_SCOPED_EXACT.forEach((k) => localStorage.removeItem(k));
    // Collect first, then delete — mutating while iterating localStorage's
    // index-based API skips entries.
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && USER_SCOPED_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage blocked (private mode, enterprise policy) — nothing to clear.
  }
}

/** Drop the service worker's cached API responses and downloaded documents.
 *  Fire-and-forget: returns a promise but callers need not await it. */
export async function clearApiCaches() {
  try {
    if (typeof caches === "undefined" || !caches?.keys) return;
    const names = await caches.keys();
    await Promise.all(
      names
        // Leave the precached app shell alone — it holds no user data and
        // evicting it would force a cold reload on every sign-out.
        .filter((n) => !/precache|shell|static/i.test(n))
        .map((n) => caches.delete(n)),
    );
  } catch {
    // Cache API unavailable or blocked — nothing to clear.
  }
}

/** Full teardown for an identity change. Safe to call more than once. */
export function clearSessionData() {
  clearUserScopedStorage();
  // Deliberately not awaited: sign-out should not block on cache eviction.
  clearApiCaches();
}

export default clearSessionData;
